import React from "react";
import { RichText } from "./RichText";
import { useI18n, pickLang } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL || "";
const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${API}${u}`);

function embedUrl(url) {
  try {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  } catch (e) { /* noop */ }
  return null;
}

function Media({ url, kind, caption, idx }) {
  if (!url) return null;
  const inner =
    kind === "video" ? (
      embedUrl(url) ? (
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          <iframe
            src={embedUrl(url)}
            title={caption || "Video"}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <video src={resolve(url)} controls className="w-full" />
      )
    ) : (
      <img src={resolve(url)} alt={caption || "Example"} loading="lazy" className="w-full object-cover" />
    );
  return (
    <figure className="rounded-2xl overflow-hidden nb-border nb-shadow-sm" data-testid={`material-media-${idx}`}>
      {inner}
      {caption && <figcaption className="text-xs text-nbblack/60 px-3 py-2">{caption}</figcaption>}
    </figure>
  );
}

function Row({ row, idx, lang }) {
  const layout = row.layout || "text";
  const kind = layout.startsWith("video") ? "video" : "image";
  const text = pickLang(row.text_md, lang);
  const caption = pickLang(row.caption, lang);

  if (layout === "text") {
    return <RichText md={text} className="text-nbblack/75 leading-relaxed" />;
  }

  if (layout === "image" || layout === "video") {
    const wide = (row.width || "wide") === "wide";
    return (
      <div className={wide ? "" : "max-w-md mx-auto"} data-testid={`material-row-${idx}`}>
        <Media url={row.media_url} kind={kind} caption={caption} idx={idx} />
      </div>
    );
  }

  // Split layouts: media on one side, text on the other.
  const mediaLeft = layout.endsWith("_left");
  const mediaEl = <Media url={row.media_url} kind={kind} caption={caption} idx={idx} />;
  const textEl = <RichText md={text} className="text-nbblack/75 leading-relaxed" />;
  return (
    <div className="grid sm:grid-cols-2 gap-5 items-center" data-testid={`material-row-${idx}`}>
      {mediaLeft ? (<>{mediaEl}<div>{textEl}</div></>) : (<><div>{textEl}</div>{mediaEl}</>)}
    </div>
  );
}

export function MaterialDescription({ desc }) {
  const { lang } = useI18n();
  if (!desc) return null;
  const rows = desc.rows || [];
  const heading = pickLang(desc.heading, lang);
  const bodyMd = pickLang(desc.body_md, lang);
  const hasContent = heading || rows.length || bodyMd;
  if (!hasContent) return null;
  return (
    <article className="bg-white nb-border nb-shadow rounded-3xl p-6 sm:p-8" data-testid="material-description">
      {heading && <h2 className="font-head font-extrabold text-2xl sm:text-3xl mb-5">{heading}</h2>}
      <div className="space-y-6">
        {rows.length > 0
          ? rows.map((r, i) => <Row key={i} row={r} idx={i} lang={lang} />)
          : bodyMd && <RichText md={bodyMd} className="text-nbblack/75 leading-relaxed" />}
      </div>
    </article>
  );
}

export default MaterialDescription;
